import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RotateCcw } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SupplierPaymentForm from '../../components/purchase/SupplierPaymentForm';
import api from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const SupplierPayments = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [payRes, supRes] = await Promise.all([
        api.get('/supplier-payments'),
        api.get('/suppliers'),
      ]);
      setPayments(payRes.data);
      setSuppliers(supRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReverse = async (payment) => {
    const supplierName = payment.supplier?.name || 'supplier';
    if (!window.confirm(`Reverse this payment of ${fmtRs(payment.amount)} to ${supplierName}? A reversal entry will be created.`)) return;
    try {
      const r = await api.post(`/supplier-payments/${payment.id}/reverse`);
      alert(r.data.message || 'Payment reversed successfully');
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reverse payment');
    }
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="supplier-payments-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="payments-title">Supplier Payments</h1>
            <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium flex items-center gap-2" data-testid="add-payment-button">
              <Plus className="w-5 h-5" /> Record Payment
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : payments.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No supplier payments recorded</p>
              <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-sm px-4 py-2 font-medium">Record First Payment</button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="payments-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Supplier</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Method</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Reference</th>
                    <th className="py-3 px-4">Notes</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const isReversed = p.status === 'reversed';
                    const isReversal = !!p.reversal_of_id;
                    return (
                      <tr key={p.id} className={`border-b border-[#e5e7eb] hover:bg-[#f9fafb] ${isReversed ? 'opacity-50' : ''}`}>
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">{p.supplier?.name || 'N/A'}</td>
                        <td className="py-3 px-4 font-semibold text-[#1E40AF]">{fmtRs(p.amount)}</td>
                        <td className="py-3 px-4 text-[#6b7280] capitalize">{(p.payment_method || '').replace('_', ' ')}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '-'}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{p.reference || '-'}</td>
                        <td className="py-3 px-4 text-[#6b7280] truncate max-w-[200px]">{p.notes || '-'}</td>
                        <td className="py-3 px-4">
                          {isReversed && <span className="px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B] text-xs rounded-sm font-medium">Reversed</span>}
                          {isReversal && <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] text-xs rounded-sm font-medium">Reversal</span>}
                          {!isReversed && !isReversal && <span className="px-2 py-0.5 bg-[#DBEAFE] text-[#1E40AF] text-xs rounded-sm font-medium">Posted</span>}
                        </td>
                        <td className="py-3 px-4">
                          {!isReversed && !isReversal && (
                            <button
                              onClick={() => handleReverse(p)}
                              className="flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`reverse-payment-${p.id}`}
                              title="Reverse this payment"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Reverse
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
      {showForm && <SupplierPaymentForm suppliers={suppliers} onClose={() => setShowForm(false)} onSuccess={() => { setShowForm(false); fetchData(); }} />}
    </div>
  );
};

export default SupplierPayments;
